import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, ShieldCheck, Leaf, Upload, Trash2, Camera, RefreshCw, CalendarClock,
  History, Pencil, Save, X,
} from "lucide-react";
import ReceiptViewer from "@/components/ReceiptViewer";

const DOC_META = {
  rc: {
    label: "Registration Certificate (RC)",
    icon: FileText,
    expiryField: null,
    fields: [
      ["registration_number", "Registration No"],
      ["owner_name", "Owner"],
      ["chassis_number", "Chassis No"],
      ["engine_number", "Engine No"],
      ["fuel_type", "Fuel Type"],
      ["vehicle_class", "Class"],
      ["maker", "Maker"],
      ["model", "Model"],
      ["colour", "Colour"],
      ["manufacture_year", "Mfg. Year"],
      ["registration_date", "Registered On"],
      ["expiry_date", "Registration Expiry"],
      ["fitness_valid_upto", "Fitness Valid Upto"],
    ],
  },
  insurance: {
    label: "Insurance Policy",
    icon: ShieldCheck,
    expiryField: "policy_end",
    fields: [
      ["policy_number", "Policy No"],
      ["insurer", "Insurer"],
      ["insured_name", "Insured Name"],
      ["registration_number", "Vehicle No"],
      ["policy_start", "Policy Start"],
      ["policy_end", "Policy End"],
      ["premium", "Premium (₹)"],
      ["coverage_type", "Coverage"],
      ["idv", "IDV (₹)"],
    ],
  },
  puc: {
    label: "PUC Certificate",
    icon: Leaf,
    expiryField: "valid_upto",
    fields: [
      ["certificate_number", "Certificate No"],
      ["registration_number", "Vehicle No"],
      ["testing_center", "Testing Centre"],
      ["fuel_type", "Fuel Type"],
      ["operator_name", "Operator"],
      ["issue_date", "Issued On"],
      ["valid_upto", "Valid Upto"],
      ["co_pct", "CO %"],
      ["hc_ppm", "HC (ppm)"],
      ["co2_pct", "CO₂ %"],
      ["smoke_density", "Smoke (HSU / K)"],
    ],
  },
};

export default function VehicleDocuments({ vehicleId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [showHistoryFor, setShowHistoryFor] = useState({}); // {rc:true, insurance:false, puc:true}
  const inputs = {
    rc: useRef(null),
    insurance: useRef(null),
    puc: useRef(null),
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}/documents`);
      setDocs(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(
        `/vehicles/${vehicleId}/documents/upload?doc_type=${docType}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }
      );
      toast.success(`${DOC_META[docType].label} uploaded — AI extracted the fields`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(`Delete this ${DOC_META[doc.doc_type].label}?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveEdits = async (doc, nextFields, nextSummary) => {
    try {
      const { data } = await api.patch(`/documents/${doc.id}`, {
        ocr_fields: nextFields,
        ocr_summary: nextSummary,
      });
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, ...data } : d)));
      toast.success("Saved");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const byType = (t) => docs.filter((d) => d.doc_type === t);

  const TypeBlock = ({ docType }) => {
    const meta = DOC_META[docType];
    const list = byType(docType);
    const active = list[0] || null; // API sorts by uploaded_at desc
    const archived = list.slice(1);
    const showHistory = !!showHistoryFor[docType];

    return (
      <div className="border border-[#262626] p-5 space-y-4" data-testid={`doc-card-${docType}`}>
        <TypeHeader
          docType={docType}
          hasActive={!!active}
          uploaded={active?.uploaded_at}
          onUpload={() => inputs[docType].current?.click()}
          uploading={uploadingType === docType}
          loading={loading}
        />
        {active ? (
          <DocumentBody
            doc={active}
            meta={meta}
            showActiveChip
            onDelete={removeDoc}
            onSave={saveEdits}
          />
        ) : (
          !loading && (
            <p className="text-sm text-[#737373]">
              No {meta.label.toLowerCase()} uploaded yet. Tap <b>Upload &amp; Scan</b> above.
            </p>
          )
        )}
        {archived.length > 0 && (
          <div className="pt-3 border-t border-[#262626]">
            <button
              onClick={() => setShowHistoryFor((s) => ({ ...s, [docType]: !showHistory }))}
              className="text-xs font-semibold text-[#A3A3A3] hover:text-white inline-flex items-center gap-2"
              data-testid={`doc-history-toggle-${docType}`}
            >
              <History size={14} />
              {showHistory ? "Hide history" : `Show history (${archived.length})`}
            </button>
            {showHistory && (
              <div className="mt-4 space-y-4">
                {archived.map((d) => (
                  <DocumentBody
                    key={d.id}
                    doc={d}
                    meta={meta}
                    onDelete={removeDoc}
                    onSave={saveEdits}
                    dim
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ft-card p-6 space-y-6" data-testid="vehicle-documents-section">
      <div>
        <p className="ft-overline mb-2">DOCUMENTS · AI EXTRACTED</p>
        <h2 className="text-2xl font-black tracking-tighter">Registration · Insurance · PUC</h2>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Upload each document. Gemini reads the details, flags renewals 30 days before expiry, and lets you edit any field manually.
        </p>
      </div>

      {["rc", "insurance", "puc"].map((t) => (
        <div key={t}>
          <input
            ref={inputs[t]}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadDoc(t, f); }}
            data-testid={`${t}-upload-input`}
          />
          <TypeBlock docType={t} />
        </div>
      ))}
    </div>
  );
}

function TypeHeader({ docType, hasActive, uploaded, onUpload, uploading, loading }) {
  const meta = DOC_META[docType];
  const Icon = meta.icon;
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#1A1A1A] flex items-center justify-center">
          <Icon size={20} className="text-[#FF5A1F]" />
        </div>
        <div>
          <p className="font-bold">{meta.label}</p>
          <p className="text-xs text-[#737373] font-mono">
            {hasActive ? `Latest uploaded ${(uploaded || "").slice(0, 10)}` : "Not uploaded yet"}
          </p>
        </div>
      </div>
      <button
        onClick={onUpload}
        disabled={uploading || loading}
        className="border border-[#262626] px-3 py-2 text-sm font-semibold hover:bg-[#1A1A1A] hover:border-[#FF5A1F]/50 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`doc-upload-${docType}`}
      >
        {uploading ? <RefreshCw size={14} className="animate-spin" /> : hasActive ? <Camera size={14} /> : <Upload size={14} />}
        {uploading ? "Scanning…" : hasActive ? "Upload new version" : "Upload & Scan"}
      </button>
    </div>
  );
}

function DocumentBody({ doc, meta, onDelete, onSave, showActiveChip, dim }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [summary, setSummary] = useState(doc.ocr_summary || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(doc.ocr_fields || {});
    setSummary(doc.ocr_summary || "");
    setEditing(false);
  }, [doc.id, doc.ocr_fields, doc.ocr_summary]);

  const fields = doc.ocr_fields || {};
  const expiryInfo = meta.expiryField ? computeExpiry(fields[meta.expiryField]) : null;

  const save = async () => {
    setSaving(true);
    const ok = await onSave(doc, draft, summary);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const cancel = () => {
    setDraft(doc.ocr_fields || {});
    setSummary(doc.ocr_summary || "");
    setEditing(false);
  };

  return (
    <div className={`space-y-4 ${dim ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {showActiveChip && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FF5A1F]/15 text-[#FF5A1F] px-2 py-1 border border-[#FF5A1F]/40">
              Active
            </span>
          )}
          {dim && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#1A1A1A] text-[#737373] px-2 py-1 border border-[#262626]">
              Archived · {(doc.uploaded_at || "").slice(0, 10)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              <button
                onClick={() => setEditing(true)}
                className="border border-[#262626] px-3 py-1.5 text-xs font-semibold hover:bg-[#1A1A1A] hover:border-[#FF5A1F]/50 transition-colors inline-flex items-center gap-2"
                data-testid={`doc-edit-${doc.doc_type}-${doc.id}`}
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                onClick={() => onDelete(doc)}
                className="border border-[#262626] px-3 py-1.5 text-xs text-[#A3A3A3] hover:text-red-400 hover:border-red-500/50 transition-colors inline-flex items-center gap-2"
                data-testid={`doc-delete-${doc.doc_type}-${doc.id}`}
              >
                <Trash2 size={12} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={save}
                disabled={saving}
                className="bg-[#FF5A1F] text-black px-3 py-1.5 text-xs font-bold hover:bg-[#E04F1A] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                data-testid={`doc-save-${doc.doc_type}-${doc.id}`}
              >
                <Save size={12} /> Save
              </button>
              <button
                onClick={cancel}
                disabled={saving}
                className="border border-[#262626] px-3 py-1.5 text-xs font-semibold hover:bg-[#1A1A1A] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                data-testid={`doc-cancel-${doc.doc_type}-${doc.id}`}
              >
                <X size={12} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {expiryInfo && !editing && (
        <div
          className={`px-4 py-3 border-l-4 ${expiryInfo.severity === "expired" ? "border-red-500 bg-red-500/10 text-red-300" : expiryInfo.severity === "warn" ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F]" : "border-emerald-500 bg-emerald-500/5 text-emerald-300"} flex items-center gap-3`}
          data-testid={`doc-expiry-banner-${doc.doc_type}`}
        >
          <CalendarClock size={18} />
          <span className="text-sm font-semibold">{expiryInfo.label}</span>
        </div>
      )}

      {!editing ? (
        <>
          {doc.ocr_summary && (
            <p className="text-sm text-[#D4D4D4] leading-relaxed border-l-2 border-[#FF5A1F]/50 pl-3 py-1 italic">
              {doc.ocr_summary}
            </p>
          )}
          {doc.file_path && (
            <div className="flex items-center gap-3">
              <ReceiptViewer path={doc.file_path} />
              <span className="text-xs text-[#737373]">Tap thumbnail to view full document</span>
            </div>
          )}
          {Object.keys(fields).length > 0 && (
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              {meta.fields.map(([key, label]) => {
                const v = fields[key];
                if (v === undefined || v === null || v === "") return null;
                return (
                  <div key={key}>
                    <dt className="text-[10px] uppercase tracking-wider text-[#737373] font-mono">{label}</dt>
                    <dd className="text-white font-mono mt-0.5 break-words">{String(v)}</dd>
                  </div>
                );
              })}
            </dl>
          )}
        </>
      ) : (
        <div className="space-y-4" data-testid={`doc-edit-form-${doc.doc_type}-${doc.id}`}>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#737373] font-mono">Summary</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="mt-1 w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF5A1F] px-3 py-2 text-sm text-white font-mono outline-none"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {meta.fields.map(([key, label]) => (
              <div key={key}>
                <label className="text-[10px] uppercase tracking-wider text-[#737373] font-mono">{label}</label>
                <input
                  type="text"
                  value={draft[key] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  className="mt-1 w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF5A1F] px-3 py-2 text-sm text-white font-mono outline-none"
                  data-testid={`doc-field-${doc.doc_type}-${key}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function computeExpiry(expiry) {
  if (!expiry) return null;
  try {
    const end = new Date(String(expiry).slice(0, 10));
    if (isNaN(end.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((end - today) / (1000 * 60 * 60 * 24));
    if (days < 0) {
      return { severity: "expired", label: `EXPIRED ${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago (${String(expiry).slice(0, 10)})` };
    }
    if (days <= 30) {
      return { severity: "warn", label: `Renewal due in ${days} day${days !== 1 ? "s" : ""} (${String(expiry).slice(0, 10)})` };
    }
    return { severity: "ok", label: `Valid — ${days} days remaining (until ${String(expiry).slice(0, 10)})` };
  } catch {
    return null;
  }
}
